import { describeError } from "../../common/rateLimit";

/* ------------------------------------------------------------------ *
 * The row
 * ------------------------------------------------------------------ */

/**
 * The `AppConfig` key holding the lowest app version still allowed.
 *
 * Nothing creates it — it is not seeded, not written at boot, and a missing row
 * is the correct default: until an admin sets one here, no upgrade is forced
 * anywhere. Clearing deletes the row again rather than storing an empty string.
 */
export const MIN_VERSION_KEY = "minVersion";

/** One row of `fetchAppConfig`, which returns the whole key/value table. */
export interface AppConfigRow {
  key: string;
  value?: string | null;
}

/**
 * The stored value exactly as stored, or null when the row does not exist.
 *
 * Deliberately not normalised, and a value the app cannot read is not filtered
 * out here: the app *ignores* such a value silently, so the only symptom of a
 * hand-edited row is a forced upgrade that never happened. It has to reach the
 * screen to be reported — see `enforcementStateOf`.
 */
export function readMinVersion(
  rows: Array<AppConfigRow> | null | undefined
): string | null {
  const row = rows?.find((entry) => entry.key === MIN_VERSION_KEY);

  return row?.value ?? null;
}

/* ------------------------------------------------------------------ *
 * What the app will accept
 * ------------------------------------------------------------------ */

/**
 * semver.org's own pattern, mirroring `min-version.config.ts` on the server —
 * leading zeros refused and all.
 *
 * The client accepts exactly this and **ignores anything else**, forcing no
 * upgrade at all. So this must never be relaxed to be friendly about `v4.0.88`
 * or `4.0`: anything accepted here that the client does not parse would look
 * set on this screen and enforce nothing, and the way that gets discovered is
 * the rollout that did not happen. The server refuses the same values again —
 * this check only saves the round trip and puts the message on the input.
 */
export const SEMVER_PATTERN =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/;

/**
 * Surrounding whitespace is trimmed before the test, as the server does — that
 * is a paste artefact rather than something anyone typed. Nothing else is
 * forgiven.
 */
export function normalizeVersion(value: string): string {
  return value.trim();
}

export function isValidVersion(value: string): boolean {
  return SEMVER_PATTERN.test(normalizeVersion(value));
}

/** The one phrasing of the rule, shared by the field's helper text and its error. */
export const VERSION_FORMAT_HINT =
  'Bare semver — three numbers, no "v" prefix, for example "4.0.88".';

/**
 * Why an input is refused, or null when it is fine to send.
 *
 * An empty box is not treated as "clear it": clearing is its own button, and a
 * mutation that both sets and unsets from the same control is one stray
 * keystroke away from quietly ending a rollout.
 */
export function validateVersion(value: string): string | null {
  const version = normalizeVersion(value);

  if (!version) {
    return "Enter a version, or use Stop forcing an upgrade to clear it.";
  }

  if (!isValidVersion(version)) {
    return `The app cannot read "${version}", so it would enforce nothing. ${VERSION_FORMAT_HINT}`;
  }

  return null;
}

/* ------------------------------------------------------------------ *
 * What is in force
 * ------------------------------------------------------------------ */

/**
 * - `unset` — no row. Nothing is forced; the shipped state.
 * - `forcing` — a value the app can read. Every install below it is forced.
 * - `inert` — a row the app cannot parse, so it is ignored and nothing is
 *   forced. `setMinAppVersion` refuses to write one, so this can only arrive by
 *   a hand-edit of the table, which is exactly why it needs saying out loud.
 */
export type EnforcementState = "unset" | "forcing" | "inert";

export function enforcementStateOf(stored: string | null): EnforcementState {
  if (stored === null || !normalizeVersion(stored)) return "unset";

  return isValidVersion(stored) ? "forcing" : "inert";
}

/** The banner above the form: the answer to "is anyone being forced right now?". */
export function describeEnforcement(stored: string | null): {
  severity: "info" | "warning" | "error";
  title: string;
  text: string;
} {
  switch (enforcementStateOf(stored)) {
    case "forcing":
      return {
        severity: "warning",
        title: `Forcing an upgrade to ${normalizeVersion(stored ?? "")}`,
        text: `Every install below ${normalizeVersion(
          stored ?? ""
        )} is shown an update prompt it cannot dismiss, at its next launch. ${LAUNCH_NOTICE}`,
      };
    case "inert":
      return {
        severity: "error",
        title: "Stored value cannot be read by the app",
        text: `The row holds "${stored}", which the app cannot parse. It ignores the value and forces no upgrade, so this reads as set while enforcing nothing. ${VERSION_FORMAT_HINT} Nothing on this screen could have written this — set a version the app can read, or clear the row.`,
      };
    default:
      return {
        severity: "info",
        title: "No forced upgrade",
        text: "No minimum version is set, so nobody is forced to update. That is the default and the shipped state — the row only exists once somebody sets it here.",
      };
  }
}

/* ------------------------------------------------------------------ *
 * Standing copy
 * ------------------------------------------------------------------ */

/**
 * There is no staged rollout on the client side, and no way past the prompt.
 * Never imply this can be tried on a slice of the fleet first.
 */
export const LAUNCH_NOTICE =
  "There is no staged rollout: every install below the value is forced at its next launch, and the prompt has no Ignore or Later.";

/**
 * The mistake this heads off: setting the value against the version of the code
 * a device is running rather than the release it reports.
 */
export const BINARY_VERSION_NOTICE =
  "Set this against what the app stores actually serve, not what the code contains. A Shorebird patch does not move the reported version — a device carrying a patched fix still reports its original release, so raising the minimum past that release sends those members to the store anyway.";

/** `AppConfig` has no audit table, so the log line is the only record. */
export const NO_AUDIT_NOTICE =
  "AppConfig keeps no audit trail. The only record of this change is a warning line in the server log (~/.pm2/pm2.log in production), naming the admin who made it.";

/** Staging exists; the client half has no way to limit the blast radius. */
export const STAGING_NOTICE =
  "Try it on staging first. Once this applies, a member below the value cannot get into the app at all until they update.";

/* ------------------------------------------------------------------ *
 * Writing
 * ------------------------------------------------------------------ */

/**
 * Toast wording for a completed write, phrased from what the server stored
 * rather than from what was typed — a refused write leaves the row untouched,
 * and the returned value is the only thing that proves which happened.
 */
export function summarizeMinVersion(
  stored: string | null,
  previous: string | null
): string {
  if (!stored) {
    return "Minimum version cleared — no upgrade is forced. Members who were being forced get back in at their next launch.";
  }

  const was = previous ? `was ${previous}` : "was unset";

  return `Minimum version set to ${stored} (${was}). Every install below it is forced to update at its next launch.`;
}

/**
 * Toast wording for a failed write. The row is untouched either way, which is
 * worth saying: the screen still shows the old value, and that is not a stale
 * render.
 */
export function describeMinVersionFailure(
  error: unknown,
  clearing: boolean
): string {
  const code = graphQLErrorCode(error);

  if (code === "INVALID_APP_VERSION") {
    // The server's own message names the value and the format. Nothing was
    // stored, so there is no half-applied state to explain.
    return (
      (error as Error | undefined)?.message ??
      "The server refused that version. Nothing was changed."
    );
  }

  if (code === "FORBIDDEN") {
    return "Your account is not allowed to change the minimum app version. Sign in as an admin and try again.";
  }

  return describeError(
    error,
    clearing
      ? "Could not clear the minimum version. Nothing was changed."
      : "Could not set the minimum version. Nothing was changed."
  );
}

function graphQLErrorCode(error: unknown): string | undefined {
  const errors = (
    error as { graphQLErrors?: Array<{ extensions?: { code?: unknown } }> }
  )?.graphQLErrors;

  const code = errors?.[0]?.extensions?.code;

  return typeof code === "string" ? code : undefined;
}
